'use client';

import React from 'react';
import { CompactScan, useTalkToUs } from '@/components/marketing';
import { HomepageScanner } from '../checker/HomepageScanner';
import type { VisibilityCheckerTransport } from '../checker/VisibilityCheckerInput';
import { useState, useCallback, useRef, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Practice } from '@/lib/ai-visibility-checker/schemas';
import { stripWww } from '@/lib/url';
import { discoveryCTAEvents } from '../PostHogProvider';
import { startSessionRecording } from '../PostHogProvider';
import { SparklesIcon, ArrowRightIcon, ClockIcon, LoadingSpinner } from '@/components/icons';
import { SCAN_SUBMIT_CTA_LABEL, SCAN_TRUST_LINE } from '@/lib/conversion-copy';
import { withDiscoverySource, type DiscoveryPageSource } from '@/lib/discovery-source';
import { readAdAttribution } from '@/lib/ad-attribution';
import {
  getReportVocabulary,
  type ReportVocabulary,
} from '@/lib/ai-visibility-checker/report-vocabulary';

const CALM_EASE = [0.33, 0, 0.2, 1] as const;

function normalizeUrl(input: string): string {
  let url = input.trim();
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }
  return url;
}

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    if (!parsed.hostname.includes('.')) return false;
    return true;
  } catch {
    return false;
  }
}

function extractNameFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const domain = stripWww(parsed.hostname);
    return domain
      .split('.')[0]
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } catch {
    return url;
  }
}

function fuzzyResetTime(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  if (minutes <= 5) return 'a few minutes';
  if (minutes <= 15) return 'about 15 minutes';
  if (minutes <= 30) return 'about half an hour';
  return 'about an hour';
}

/**
 * Copy per embedding page, with customer/business or patient/practice nouns
 * selected by the embedding page's audience.
 */
function headlines(v: ReportVocabulary): Record<string, { heading: string; subheading: string }> {
  return {
    learn: {
      heading: `See if ${v.patients} can find you`,
      subheading: `Run a free scan to see how your ${v.practice} appears in Google search and ChatGPT.`,
    },
    market: {
      heading: `See where ${v.patients} can find you`,
      subheading: `Run a free scan to see how your ${v.practice} appears in Google search and ChatGPT for your specialty.`,
    },
    compare: {
      heading: `See where your ${v.practice} stands`,
      subheading: `Run a free scan to see how your ${v.practice} appears in Google search and ChatGPT before deciding what to improve.`,
    },
    problem: {
      heading: 'Find gaps in your visibility',
      subheading: `Get a free scan with suggestions to help ${v.patients} find your ${v.practice} in Google search and ChatGPT.`,
    },
  };
}

interface InlineVisibilityCheckerProps {
  source: DiscoveryPageSource;
  /**
   * Whether the embedding page speaks to healthcare practices. Required so
   * every embed states its audience: healthcare content pages pass `true` and
   * keep patient/practice wording; anything else reads customer/business.
   */
  isHealthcare: boolean;
  specialty?: string;
  className?: string;
  visualVariant?: 'legacy' | 'articleV8' | 'marketing';
  /**
   * Marketing variant only. `close` renders the midnight closing band for a
   * scan that ends the page; the caller renders it outside `Rail`, since the
   * band carries its own. Default `inline` is the sage card that articles keep
   * mid-page above their related links.
   */
  placement?: 'inline' | 'close';
  transport?: VisibilityCheckerTransport;
}

export function InlineVisibilityChecker({
  source,
  isHealthcare,
  specialty,
  className = '',
  visualVariant = 'legacy',
  placement = 'inline',
  transport = fetch,
}: InlineVisibilityCheckerProps): React.ReactElement {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    resetIn: string;
    resetInSeconds: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const onTalkToUs = useTalkToUs(`${source}-scan`);

  const vocabulary = getReportVocabulary(isHealthcare);
  const content = headlines(vocabulary)[source];
  const subheading = specialty
    ? `See how your ${specialty.toLowerCase()} ${vocabulary.practice} appears when ${vocabulary.patients} search in ChatGPT and Google.`
    : content.subheading;
  const siteLabel = `Your ${vocabulary.practice} website`;
  const sitePlaceholder = `your${vocabulary.practice}.com`;

  const getValidatedPractice = useCallback((): Practice | null => {
    const trimmed = inputValue.trim();
    if (!trimmed) return null;
    const normalizedUrl = normalizeUrl(trimmed);
    if (!isValidUrl(normalizedUrl)) return null;
    return {
      placeId: normalizedUrl,
      name: extractNameFromUrl(normalizedUrl),
      address: '',
      website: normalizedUrl,
    };
  }, [inputValue]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      if (error || rateLimitInfo) {
        setError(null);
        setRateLimitInfo(null);
      }
    },
    [error, rateLimitInfo]
  );

  const handleFocus = useCallback(() => {
    discoveryCTAEvents.inputFocused(source);
  }, [source]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const practice = getValidatedPractice();

      if (!practice) {
        setError(`Please enter a valid website (e.g., ${sitePlaceholder})`);
        inputRef.current?.focus();
        return;
      }

      if (isSubmitting) return;

      setIsSubmitting(true);
      setError(null);
      setRateLimitInfo(null);

      startSessionRecording();
      discoveryCTAEvents.inputSubmitted({ source, url: practice.website ?? '' });

      try {
        const response = await transport('/api/tools/ai-visibility-checker/create-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            practice,
            hp_website: honeypot,
            attribution: readAdAttribution() ?? undefined,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          if (response.status === 429 && errorData.code === 'RATE_LIMITED') {
            setRateLimitInfo({
              resetIn: errorData.resetIn || 'later',
              resetInSeconds: errorData.resetInSeconds || 3600,
            });
            setIsSubmitting(false);
            return;
          }

          throw new Error(errorData.error || 'Failed to create check');
        }

        const { url } = await response.json();
        // Carry the originating page onto the report URL. The check row has no
        // `source` column, so this link is the only channel that survives the
        // new-tab hand-off and lets the report's own conversion events
        // (`check_started`, `email_captured`) report where the check came from.
        const newTab = window.open(withDiscoverySource(url, source), '_blank');
        if (newTab) newTab.focus();

        setInputValue('');
        setIsSubmitting(false);
      } catch (err) {
        console.error('Failed to create check:', err);
        setError('Something went wrong. Please try again.');
        setIsSubmitting(false);
      }
    },
    [getValidatedPractice, isSubmitting, source, honeypot, transport, sitePlaceholder]
  );

  // The closing scan offer: the hero's form (HomepageScanner) inside the shared
  // CompactScan, driven by this component's own submit path so the `source`
  // attribution and discovery events above are unchanged. Off healthcare the
  // label is the homepage's "Your website"; healthcare keeps its practice noun.
  if (visualVariant === 'marketing') {
    const scanner = (
      <HomepageScanner
        inputId="inline-practice-url"
        inputRef={inputRef}
        inputWrapperRef={inputWrapperRef}
        inputValue={inputValue}
        isSubmitting={isSubmitting}
        buttonState={isSubmitting ? 'submitting' : 'idle'}
        error={error}
        rateLimitInfo={rateLimitInfo}
        honeypot={honeypot}
        placeholder={sitePlaceholder}
        ctaLabel={SCAN_SUBMIT_CTA_LABEL}
        label={isHealthcare ? siteLabel : 'Your website'}
        showTrustSignal
        handleSubmit={handleSubmit}
        handleInputChange={handleInputChange}
        handleFocus={handleFocus}
        setHoneypot={setHoneypot}
      />
    );
    return placement === 'close' ? (
      <CompactScan
        tone="close"
        heading={content.heading}
        description={subheading}
        className={className}
      >
        {scanner}
      </CompactScan>
    ) : (
      <CompactScan
        heading={content.heading}
        description={subheading}
        onTalkToUs={onTalkToUs}
        className={className}
      >
        {scanner}
      </CompactScan>
    );
  }

  if (visualVariant === 'legacy') {
    return (
      <motion.section
        id="visibility-check"
        className={`relative mt-16 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 p-8 sm:p-12 ${className}`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: CALM_EASE }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#B45309]/10 blur-3xl" />

        <div className="relative mx-auto max-w-xl">
          <div className="mb-4 flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-[#B45309]" />
            <span className="text-sm font-medium text-slate-300">Free visibility scan</span>
          </div>

          <h2 className="font-display text-2xl font-medium tracking-tight text-white sm:text-3xl">
            {content.heading}
          </h2>
          <p className="mt-3 text-slate-300">{subheading}</p>

          <form onSubmit={handleSubmit} noValidate className="mt-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label
                  htmlFor="inline-practice-url"
                  className="mb-1.5 block text-sm font-medium text-slate-300"
                >
                  {siteLabel}
                </label>
                <input
                  ref={inputRef}
                  id="inline-practice-url"
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  disabled={isSubmitting}
                  placeholder={sitePlaceholder}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'inline-url-error' : undefined}
                  className={`
                    w-full rounded-xl border px-4 py-3.5
                    ${error ? 'border-red-400' : 'border-slate-600'}
                    bg-slate-800/50 text-white
                    placeholder:text-slate-500
                    focus:border-[#B45309] focus:outline-none focus:ring-2
                    ${error ? 'focus:ring-red-500/20' : 'focus:ring-[#B45309]/20'}
                    hover:border-slate-500
                    disabled:cursor-not-allowed disabled:opacity-50
                    transition-all duration-200 text-base
                  `}
                />
              </div>
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="
                  inline-flex items-center justify-center gap-2
                  whitespace-nowrap rounded-xl
                  bg-[#B45309] px-6 py-3.5
                  text-base font-semibold text-white
                  shadow-lg shadow-[#B45309]/20
                  hover:bg-[#92400E] hover:shadow-xl hover:shadow-[#B45309]/25
                  disabled:cursor-not-allowed disabled:opacity-50
                  transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-[#B45309] focus:ring-offset-2 focus:ring-offset-slate-900
                "
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner className="h-4 w-4 animate-spin" />
                    <span>Starting scan...</span>
                  </>
                ) : (
                  <>
                    <span>{SCAN_SUBMIT_CTA_LABEL}</span>
                    <ArrowRightIcon className="h-4 w-4" variant="stroked" />
                  </>
                )}
              </motion.button>
            </div>

            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="absolute -left-[9999px]"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <AnimatePresence mode="wait">
              {rateLimitInfo && (
                <motion.div
                  key="rate-limit"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2, ease: CALM_EASE }}
                  className="mt-3 rounded-xl border border-amber-700/50 bg-amber-900/30 p-3"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-start gap-3">
                    <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-amber-200">
                        You&apos;ve reached the free scan limit for now
                      </p>
                      <p className="mt-1 text-sm text-amber-300/80">
                        You can try again in {fuzzyResetTime(rateLimitInfo.resetInSeconds)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {error && !rateLimitInfo && (
                <motion.p
                  key="error"
                  id="inline-url-error"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2, ease: CALM_EASE }}
                  className="mt-2 text-sm text-red-400"
                  role="alert"
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>
          </form>

          <p className="mt-4 flex items-center gap-2 text-sm text-slate-400">
            <ClockIcon className="h-4 w-4 flex-shrink-0" />
            <span>{SCAN_TRUST_LINE}</span>
          </p>
        </div>
      </motion.section>
    );
  }

  return (
    <section
      id="visibility-check"
      className={`mt-16 rounded-[18px] border border-[#EBE6DF] bg-[#FAF7F3] p-6 sm:p-8 ${className}`}
    >
      <div className="mx-auto max-w-xl">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          Free visibility scan
        </p>

        <h2 className="mt-2 font-display text-3xl font-normal tracking-tight text-slate-900">
          {content.heading}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{subheading}</p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="mt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label
                htmlFor="inline-practice-url"
                className="mb-1.5 block text-xs font-semibold text-zinc-700"
              >
                {siteLabel}
              </label>
              <input
                ref={inputRef}
                id="inline-practice-url"
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                disabled={isSubmitting}
                placeholder={sitePlaceholder}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck="false"
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? 'inline-url-error' : undefined}
                className={`
                  w-full rounded-lg border bg-white px-4 py-3.5 text-slate-900
                  ${error ? 'border-red-700 focus:border-red-700 focus:ring-red-700 focus:ring-offset-[#B45309]' : 'border-[#D8D1C9] focus:border-[#B45309] focus:ring-[#B45309] focus:ring-offset-[#FAF7F3]'}
                  placeholder:text-slate-400
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  hover:border-[#BFB5AA]
                  disabled:cursor-not-allowed disabled:opacity-50
                  transition-colors duration-150 text-base
                `}
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                inline-flex items-center justify-center gap-2
                whitespace-nowrap rounded-full
                bg-[#B45309] px-6 py-3.5
                text-base font-semibold text-white
                hover:bg-[#92400E]
                disabled:cursor-not-allowed disabled:opacity-50
                transition-colors duration-150
                focus:outline-none focus:ring-2 focus:ring-[#B45309] focus:ring-offset-2 focus:ring-offset-[#FAF7F3]
              "
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner className="h-4 w-4 animate-spin" />
                  <span>Starting scan...</span>
                </>
              ) : (
                <>
                  <span>{SCAN_SUBMIT_CTA_LABEL}</span>
                  <ArrowRightIcon className="h-4 w-4" variant="stroked" />
                </>
              )}
            </button>
          </div>

          {/* Honeypot */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="absolute -left-[9999px]"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {rateLimitInfo && (
            <div
              className="mt-3 rounded-lg border border-amber-700/30 bg-amber-50 p-3"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start gap-3">
                <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B45309]" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    You&apos;ve reached the free scan limit for now
                  </p>
                  <p className="mt-1 text-sm text-amber-800">
                    You can try again in {fuzzyResetTime(rateLimitInfo.resetInSeconds)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && !rateLimitInfo && (
            <p id="inline-url-error" className="mt-2 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
        </form>

        {/* Trust signal */}
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
          <ClockIcon className="h-4 w-4 flex-shrink-0" />
          <span>{SCAN_TRUST_LINE}</span>
        </p>
      </div>
    </section>
  );
}
